import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Medication, MedicationRepositoryService, ReportControl } from '@xmedical/core';
import { ReportControlEditorService } from '../../services/report-control-editor.service';

/**
 * MedicationListRendererComponent
 *
 * Renders a list of patient medications in a report.
 * Uses a refreshTrigger input signal instead of PatientEventsService.
 * The consuming app should increment refreshTrigger when medications change.
 */
@Component({
  selector: 'xmed-medication-list-renderer',
  standalone: true,
  imports: [
    MatListModule,
    MatFormFieldModule
  ],
  templateUrl: './medication-list-renderer.component.html',
  styleUrl: './medication-list-renderer.component.scss'
})
export class MedicationListRendererComponent implements OnInit {
  private medicationRepository = inject(MedicationRepositoryService);
  private editorService = inject(ReportControlEditorService);

  patientId = input.required<number>();
  control = input.required<ReportControl>();
  refreshTrigger = input<number>(0);

  medicationList = signal<Medication[]>([]);

  // Effect to reload when refreshTrigger changes
  private refreshEffect = effect(() => {
    const trigger = this.refreshTrigger();
    const pid = this.patientId();
    if (trigger > 0 && pid) {
      this.loadMedicationList();
    }
  });

  ngOnInit(): void {
    this.medicationList.set(this.control().medicationListValue || []);
    this.loadMedicationList();
  }

  /**
   * "Paracetamol - J06.9", or just "Paracetamol" when the control is set not to print diagnosis
   * codes, or when the item has no code recorded.
   */
  describe(item: Medication): string {
    return this.control().displayMedicationDiagnosisCode && item.diagnosisCode
      ? `${item.description} - ${item.diagnosisCode}`
      : item.description;
  }

  private loadMedicationList(): void {
    this.medicationRepository.loadPatientMedication(this.patientId()).subscribe(medication => {
      const sorted = this.filterToScriptType(medication).sort((a, b) => a.createdDate > b.createdDate ? 1 : -1);
      this.medicationList.set(sorted);
      this.control().medicationListValue = sorted;
      this.editorService.triggerEvent();
    });
  }

  /**
   * Narrows the patient's medication to the script type this control is configured for.
   *
   * A control with no script type prints everything. Medication controls had no filter at all before
   * this existed, so every already-saved control comes back with a null here and must keep printing
   * the full list.
   */
  private filterToScriptType(medication: Medication[]): Medication[] {
    const scriptType = this.control().scriptType;
    if (scriptType === null || scriptType === undefined) {
      return medication;
    }
    return medication.filter(item => item.scriptType === scriptType);
  }
}
